import { useEffect, useState } from "react";
import { AlertTriangle, ShieldCheck } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Sprint 2H P4 — <ConfirmationGate>
 *
 * Reusable per-action confirmation modal for all 8 Sprint 2 gated actions.
 * Anti-fatigue mandate: ONE screen, ONE checkbox, ONE button. No walls of text.
 *
 * Usage pattern:
 *   const [gateOpen, setGateOpen] = useState(false);
 *   const [gateConfig, setGateConfig] = useState(null);
 *
 *   const submit = async (body) => {
 *     try {
 *       await api.post(url, body);
 *     } catch (e) {
 *       if (e.response?.status === 400 && e.response.data?.detail?.error === "confirmation_required") {
 *         setGateConfig({ ...e.response.data.detail.gate_config, __body: body, __url: url });
 *         setGateOpen(true);
 *       } else throw e;
 *     }
 *   };
 *
 *   <ConfirmationGate open={gateOpen} onOpenChange={setGateOpen} config={gateConfig}
 *                     onConfirmed={() => { setGateOpen(false); reload(); }} />
 */

const TITLES = {
  LEVY_ISSUE: "Confirm levy issuance",
  LEVY_ISSUE_BULK: "Confirm bulk levy issuance",
  LEVY_MARK_PAID: "Confirm levy paid",
  MEETING_NOTICE: "Confirm meeting notice dispatch",
  MINUTES_PUBLISH: "Confirm minutes publication",
  BYLAW_CREATE: "Confirm new by-law",
  BYLAW_REPEAL: "Confirm by-law repeal",
  MEMBER_INVITE: "Confirm member invitation",
};

const ACK_TEXT = (
  "I understand this is a calculated/assisted output, not advice, and I'm responsible " +
  "for verifying it before relying on it."
);

export default function ConfirmationGate({ open, onOpenChange, config, onConfirmed }) {
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sandbox, setSandbox] = useState(false);

  useEffect(() => {
    if (open) {
      setChecked(false);
      api.get("/health").then((r) => setSandbox(r.data.sandbox_mode === true)).catch(() => {});
    }
  }, [open]);

  if (!config) return null;

  const {
    action_type, calculation_hash, requires_legislation_version_id,
    terms_version, __body, __url, __method = "post",
  } = config;
  const title = TITLES[action_type] || action_type;
  const snap = __body?.calculation_snapshot || config.calculation_snapshot || {};

  const doConfirm = async () => {
    setSubmitting(true);
    try {
      // Fetch current legislation_version_id for the scheme if required.
      let legId = null;
      if (requires_legislation_version_id && config.__scheme_id) {
        try {
          const { data } = await api.get(`/schemes/${config.__scheme_id}/legislation-current`);
          legId = data.legislation_version_id;
        } catch {}
      }
      const body = {
        ...__body,
        confirmation: {
          acknowledged: true,
          calculation_hash,
          terms_version,
          ...(legId ? { legislation_version_id: legId } : {}),
        },
      };
      const { data: responseData } = await api[__method](__url, body);
      toast.success("Confirmed. Recorded to audit trail.");
      onConfirmed?.(responseData);
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="confirmation-gate-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color: "var(--navy)" }}>
            <ShieldCheck size={18} style={{ color: "var(--blue)" }} />
            {title}
          </DialogTitle>
          <DialogDescription className="text-xs">
            This action is recorded permanently in your scheme's audit trail.
          </DialogDescription>
        </DialogHeader>

        {sandbox && (
          <div
            className="rounded-md p-2 text-[11px] font-bold uppercase tracking-wider text-center"
            style={{ background: "#fee2e2", color: "#991b1b", border: "1px solid #fecaca" }}
            data-testid="gate-placeholder-banner"
          >
            Placeholder copy — not lawyer-reviewed — not for pilot use
          </div>
        )}

        {/* Calculation snapshot */}
        {Object.keys(snap).length > 0 && (
          <div className="rounded-md border overflow-hidden" style={{ borderColor: "var(--border)" }}>
            <table className="w-full text-xs" data-testid="gate-calculation-snapshot">
              <tbody>
                {Object.entries(snap).map(([k, v]) => (
                  <tr key={k} className="border-b last:border-0" style={{ borderColor: "var(--border)" }}>
                    <td className="px-3 py-1.5 font-medium capitalize" style={{ color: "var(--slate)" }}>
                      {k.replace(/_/g, " ")}
                    </td>
                    <td className="px-3 py-1.5" style={{ color: "var(--navy)" }}>
                      {typeof v === "object" ? JSON.stringify(v) : String(v)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Legal basis */}
        {requires_legislation_version_id && (
          <div className="text-xs" style={{ color: "var(--slate)" }} data-testid="gate-legal-basis">
            <AlertTriangle size={11} className="inline mr-1" />
            The Act requires this action to be recorded against the current legislation
            version for your jurisdiction. This is captured automatically on confirm.
          </div>
        )}

        {/* Acknowledgement checkbox */}
        <label
          className="flex items-start gap-2 rounded-md p-3 cursor-pointer"
          style={{ background: "#f8fafc" }}
        >
          <Checkbox
            checked={checked}
            onCheckedChange={setChecked}
            data-testid="gate-ack-checkbox"
            className="mt-0.5"
          />
          <span className="text-xs" style={{ color: "var(--navy)" }}>{ACK_TEXT}</span>
        </label>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="gate-cancel">
            Cancel
          </Button>
          <Button
            onClick={doConfirm}
            disabled={!checked || submitting}
            style={{ background: "var(--blue)", color: "white" }}
            data-testid="gate-confirm-button"
          >
            {submitting ? "Confirming…" : "I confirm"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * useConfirmationGate — helper hook that wraps an action call, intercepts the
 * 400 confirmation_required response, and pops the modal. Returns
 * { fire, gateProps } — spread gateProps onto <ConfirmationGate/>.
 *
 * Example:
 *   const gate = useConfirmationGate({ onConfirmed: reload });
 *   const submit = () => gate.fire("post", `/schemes/${sid}/bylaws`, body, { __scheme_id: sid });
 *   <ConfirmationGate {...gate.gateProps} />
 */
export function useConfirmationGate({ onConfirmed } = {}) {
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState(null);

  const fire = async (method, url, body, extras = {}) => {
    try {
      const res = await api[method](url, body);
      return res.data;
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 400 && d?.error === "confirmation_required") {
        setConfig({
          ...d.gate_config,
          __body: body,
          __url: url,
          __method: method,
          calculation_snapshot: body,
          ...extras,
        });
        setOpen(true);
        return null;
      }
      throw e;
    }
  };

  return {
    fire,
    gateProps: {
      open,
      onOpenChange: setOpen,
      config,
      onConfirmed: (data) => { setOpen(false); onConfirmed?.(data); },
    },
  };
}
